import { useContext, useEffect, useState } from "react";
import hash from "object-hash";
import { toast } from "react-toastify";

import "./Entities.scss";

import { Subtitle, Title } from "../../../Components/Typography";
import { LogoSection } from "../../../Components/LogoSection";
import useResponsive from "../../../hooks/useResponsive";
import { Table } from "../../../Components/Table";
import { LoadingGears as Loading } from "../../../Components/Loading";
import { IconCreate } from "../../../Components/Icons";
import { Tooltip } from "../../../Components/Tooltip";
import { ModalActionsEnum } from "../../../types/ModalActionsEnum";
import { ModalCrud } from "../../../Components/Modal";
import { ModalCrudAnillado } from "../../../Components/Modal";
import { EntitiesContext, EntitiesContextProps } from "../../../Contexts/EntitiesContext";

const tableCollections = ["anillado", "color", "faz", "hoja", "papel"];

export const Entities = () => {
  const { isDesktopOrLaptop } = useResponsive();
  const {  
    refreshEntities,
    refreshEntity,
    entities,
    loading,
    loadingCollection,
  }: EntitiesContextProps = useContext<any>(EntitiesContext);

  const [modalAction, setModalAction] = useState<any>(null);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  useEffect(() => {
    if (initialLoad) {
      refreshEntities();
      setInitialLoad(false);
    }
  }, [refreshEntities, initialLoad]);

  const onActionUpdate = (item: any) => {
    setModalAction({
      item,
      collection: item.collection,
      action: ModalActionsEnum.UPDATE,
    });
  };

  const onActionDelete = (item: any) => {
    setModalAction({
      item,
      collection: item.collection,
      action: ModalActionsEnum.DELETE,
    });
  };

  const onClickCreate = (collection: any) => {
    setModalAction({
      collection,
      action: ModalActionsEnum.CREATE,
    });
  };

  const onModalConfirm = (action: ModalActionsEnum, collection: string, item: any) => {
    let message;

    const upperCaseCollection =
      collection.charAt(0).toUpperCase() + collection.slice(1);

    if (action === ModalActionsEnum.CREATE)
      message = `"${upperCaseCollection}" creado exitosamente!`;
    if (action === ModalActionsEnum.UPDATE)
      message = `"${upperCaseCollection}" actualizado exitosamente!`;
    if (action === ModalActionsEnum.DELETE)
      message = `"${upperCaseCollection}" eliminado exitosamente!`;

    toast.success(message);
    setModalAction(null)
    refreshEntity(collection);
  };

  const getColumns = (collection: string) => {
    const anilladoColumns = [
      { key: "min_hojas", text: "Cant. Min", align: "left" },
      { key: "max_hojas", text: "Cant. Max", align: "left" },
      { key: "sheetText", text: "Hoja", align: "center" },
      { key: "precio", text: "Precio", align: "right" },
      { key: "actions", text: "Acciones", align: "right" },
    ];

    const columns = [
      { key: "id", text: "ID" },
      { key: "name", text: "Nombre" },
      { key: "actions", text: "Acciones", align: "right" },
    ];

    return collection === "anillado" ? anilladoColumns : columns;
  };

  const getFormattedRow = (row: any, collection: string) => {
    return collection === "anillado"
      ? row.map((item: any) => ({
          ...item,
          collection,
          sheetText: entities.hoja.filter((hojaItem: any) => hojaItem.id === item.id_hoja)[0].name 
        }))
      : row.map((item: any) => ({
          ...item,
          collection,
        }));
  };

  return (
    <div
      className={
        isDesktopOrLaptop ? "admin-entities-desktop" : "admin-entities-mobile"
      }
    >
      <LogoSection showInstructions={false}/>

      <div className="main-column">
        <div className="top-content">
          <div className="title-section">
            <Title>Entidades</Title>
          </div>

          {loading ? (
            <Loading />
          ) : (
            tableCollections.map((collection) => (
              <div key={hash(collection)} className="table-section">
                <div className="title">
                  <Subtitle>
                    {collection.charAt(0).toUpperCase() + collection.slice(1)}
                  </Subtitle>
                  <Tooltip text="Nuevo">
                    <IconCreate onClick={() => onClickCreate(collection)} />
                  </Tooltip>
                </div>
                <Table
                  loading={loadingCollection === collection}
                  columns={getColumns(collection)}
                  rows={getFormattedRow(entities[collection], collection)}
                  onActionUpdate={onActionUpdate}
                  onActionDelete={onActionDelete}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {modalAction && modalAction.collection !== "anillado" && (
        <ModalCrud
          modalAction={modalAction}
          setModalAction={setModalAction}
          onConfirm={onModalConfirm}
        />
      )}

      {modalAction && modalAction.collection === "anillado" && (
        <ModalCrudAnillado
          modalAction={modalAction}
          setModalAction={setModalAction}
          onConfirm={onModalConfirm}
          hojas={entities.hoja}
        />
      )}
    </div>
  );
};
